import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import ApiLoader from "../common/loader";
import axios from "axios";
import { REACT_APP_API_KEY, REACT_APP_API_URL } from "../common/config";
import { toast } from "react-toastify";

// Signature Canvas Component (reused pattern from bootAuthorizationForm)
const InitialsCanvas = ({ initialsData, onInitialsChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasInitials, setHasInitials] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && initialsData) {
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasInitials(true);
      };
      img.src = initialsData;
    }
  }, [initialsData]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    ctx.beginPath();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;

    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasInitials(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onInitialsChange(dataUrl);
    }
  };

  const clearInitials = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInitials(false);
    onInitialsChange('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={300}
        height={120}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        style={{
          border: '2px solid #ccc',
          borderRadius: '4px',
          cursor: 'crosshair',
          touchAction: 'none',
          width: '100%',
          maxWidth: '300px',
          height: '120px',
          backgroundColor: '#fff'
        }}
      />
      {hasInitials && (
        <button
          type="button"
          onClick={clearInitials}
          className="btn btn-sm btn-danger"
          style={{ marginTop: '10px' }}
        >
          Clear
        </button>
      )}
    </div>
  );
};

const FirewatchInitials = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [officerData, setOfficerData] = useState(null);
  const [error, setError] = useState(null);
  const [initialsImage, setInitialsImage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid initials link');
      setLoading(false);
      return;
    }
    fetchInitialsData();
  }, [token]);

  const fetchInitialsData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${REACT_APP_API_URL}firewatch-initials/${token}`,
        {
          headers: {
            'api-key': REACT_APP_API_KEY,
          },
        }
      );

      if (response.data.success) {
        setOfficerData(response.data.data);
      } else if (response.data.already_submitted) {
        setError('Initials have already been submitted. Thank you!');
      } else {
        setError(response.data.message || 'Failed to load initials request');
      }
    } catch (err) {
      console.error('Error fetching initials data:', err);
      if (err.response?.status === 410) {
        setError('This initials link has expired. Please request a new one.');
      } else if (err.response?.status === 404) {
        setError('Invalid or expired initials link.');
      } else {
        setError(err.response?.data?.message || 'Failed to load initials request');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!initialsImage) {
      toast.error('Please draw your initials before submitting.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}firewatch-initials/submit`,
        {
          token,
          initials_image: initialsImage,
        },
        {
          headers: {
            'api-key': REACT_APP_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 && response.data.success) {
        setSubmitted(true);
        toast.success("Initials submitted successfully!");
      } else {
        toast.error(response.data.message || "Failed to submit initials.");
      }
    } catch (err) {
      console.error("Error submitting initials:", err);
      toast.error(err.response?.data?.message || "Failed to submit initials.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '40px' }}>
        <div className="row justify-content-center">
          <div className="col-md-8 text-center">
            <ApiLoader />
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ marginTop: '40px' }}>
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card shadow">
              <div className="card-body text-center">
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
                <h4>{error}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="container" style={{ marginTop: '40px' }}>
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card shadow">
              <div className="card-body text-center">
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
                <h4>Thank you for submitting your initials!</h4>
                <p>Your initials have been saved and will be used for the firewatch logs.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ marginTop: '40px' }}>
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow">
            <div className="card-header" style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
              <h4 className="mb-0 text-center">Firewatch Initials</h4>
            </div>
            <div className="card-body">
              <h5 className="mb-3">
                Thank you, {officerData?.officer_first_name} {officerData?.officer_last_name}.
              </h5>
              <p>
                Please enter your initials below for the firewatch logs. Once done, click "Submit".
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="form-label fw-bold">Draw Your Initials</label>
                  <InitialsCanvas
                    initialsData={initialsImage}
                    onInitialsChange={setInitialsImage}
                  />
                </div>

                <div className="d-grid">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={!initialsImage || loading}
                  >
                    {loading ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirewatchInitials;
